import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { REFERRALS_MAX_PER_PAGE } from '@manaro/contracts'
import { ApiError } from '@/shared/api/client.ts'
import { queryKeys } from '@/shared/api/query-keys.ts'
import { REFERRALS_PAGE_SIZE, fetchReferral, fetchReferrals, revertAttribution } from './api.ts'
import { collectAllReferrals, downloadReferralsCsv } from './export-csv.ts'
import type { ReferralFilters } from './filters.ts'

export function useReferrals(filters: ReferralFilters) {
  return useQuery({
    queryKey: queryKeys.referralList(filters),
    queryFn: ({ signal }) => fetchReferrals(filters, REFERRALS_PAGE_SIZE, signal),
    placeholderData: keepPreviousData,
    staleTime: 30 * 1000,
  })
}

export function useReferral(code: string) {
  return useQuery({
    queryKey: queryKeys.referral(code),
    queryFn: ({ signal }) => fetchReferral(code, signal),
    retry: (attempt, error) => attempt < 2 && !(error instanceof ApiError && error.status >= 400 && error.status < 500),
    staleTime: 30 * 1000,
  })
}

export function useRevertAttribution() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) => revertAttribution(id, reason),
    onSettled: () => queryClient.invalidateQueries({ queryKey: queryKeys.referrals }),
  })
}

export function useExportReferrals() {
  return useMutation({
    mutationFn: async (filters: ReferralFilters) => {
      const rows = await collectAllReferrals((page) => fetchReferrals({ ...filters, page }, REFERRALS_MAX_PER_PAGE))
      downloadReferralsCsv(rows, filters.ownerType)
    },
  })
}
